import { useRef, useState, useEffect } from 'react';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function RevealSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const testimonials = [
  {
    name: 'Ana Paula Silva',
    role: 'CEO — Agência Criativa',
    avatar: 'AP',
    text: 'O WZap PRO transformou nossa comunicação com clientes. Disparamos campanhas para 5.000 contatos em minutos. A taxa de abertura é absurda — quase 95%! Reduzimos nosso CAC em 40%.',
    rating: 5,
  },
  {
    name: 'Marcos Oliveira',
    role: 'Diretor Comercial — MultiShop',
    avatar: 'MO',
    text: 'Elegante, rápido e completo. Conectamos nossa API Meta em menos de 2 minutos. Os relatórios em tempo real nos ajudam a tomar decisões rápidas. Suporte nota 10!',
    rating: 5,
  },
  {
    name: 'Juliana Costa',
    role: 'Marketing — Saúde Total',
    avatar: 'JC',
    text: 'Migramos de outro software e foi a melhor decisão. A segmentação avançada permitiu campanhas 3x mais eficientes. O recurso de automação economiza horas da nossa equipe todo mês.',
    rating: 5,
  },
  {
    name: 'Rafael Mendes',
    role: 'Fundador — E-commerce RJ',
    avatar: 'RM',
    text: 'O melhor custo-benefício do mercado. Testamos 4 plataformas antes de escolher o WZap PRO. A integração com Evolution API é impecável e a multi-instância é um diferencial enorme.',
    rating: 5,
  },
  {
    name: 'Patrícia Lemos',
    role: 'Gerente — Imobiliária Lemos',
    avatar: 'PL',
    text: 'Nossos corretores amaram! Enviamos novos lançamentos para listas segmentadas por bairro e perfil. As respostas chegam em minutos. Fechamos 12 vendas diretas no último mês.',
    rating: 5,
  },
];

export default function LandingTestimonials() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="testimonials" className="py-16 md:py-24 px-5 md:px-8 bg-background-50 dark:bg-[#08080d]">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-[0.2em] mb-3">Depoimentos</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">
              O que nossos clientes<br />estão dizendo
            </h2>
            <p className="text-foreground-500 dark:text-foreground-400 max-w-xl mx-auto text-base">
              Empresas de todos os tamanhos confiam no WZap PRO para escalar sua comunicação via WhatsApp.
            </p>
          </div>
        </RevealSection>

        <div className="relative max-w-4xl mx-auto">
          {/* Cards container */}
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {testimonials.map((t, i) => (
                <div key={i} className="w-full flex-shrink-0 px-4">
                  <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-7 md:p-9">
                    <div className="flex items-center gap-1 mb-5">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <i key={s} className="ri-star-fill text-amber-400 text-sm" />
                      ))}
                    </div>
                    <blockquote className="text-foreground-700 dark:text-foreground-300 text-base md:text-lg leading-relaxed mb-8 italic">
                      &ldquo;{t.text}&rdquo;
                    </blockquote>
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-500/20 dark:to-primary-500/30 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary-700 dark:text-primary-300">{t.avatar}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Nav dots */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                  i === activeIndex
                    ? 'bg-primary-500 w-7'
                    : 'bg-background-300 dark:bg-background-400 hover:bg-background-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}